import React from 'react';
import { useQuery } from '@apollo/client/react';
import { useParams } from 'react-router';
import { MainView } from '@repo/ui/Views';
import { FallbackCard } from '@repo/ui/Card';
import { useUser } from '@repo/hooks/useUser';
import type { Mercury } from '@repo/types/Models';

import { Authorization, POLICIES } from '@/lib/Authorization';
import SmsForward from '@/features/dids/components/SmsForward';
import MmsForward from '@/features/dids/components/MmsForward';
import DidsForResourceTable from '@/features/dids/components/DidsForResourceTable';
import { useHasDids } from '@/features/dids/hooks/useHasDids';
import { useSwitch } from '../hooks/useSwitch';
import { useDefaultSwitch } from '../hooks/useDefaultSwitch';
import { useSwitchNumberCount } from '../hooks/useSwitchNumberCount';
import type { AffectedNumbers } from '../lib/sharedSwitchConfirmation';
import GET_DIDS_FOR_SWITCH from '@/features/dids/gql/getDidsForSwitch.gql';
import UpdateSwitchForm from '../components/UpdateSwitchForm';
import DeleteSwitch from '../components/DeleteSwitch';
import SharedSwitchBanner from '../components/SharedSwitchBanner';
import SwitchTabNav, { switchTabPath, type SwitchTab } from '../components/SwitchTabNav';
import { SWITCH_EXPLANATION } from '../consts';

// How many affected numbers the confirmation dialog lists
const AFFECTED_LIST_SIZE = 100;

interface AccountSwitchDetailProps {
  tab: SwitchTab;
}

const AccountSwitchDetail = ({ tab }: AccountSwitchDetailProps) => {
  const { accountId, switchId } = useParams();
  const user = useUser();

  const { switch: sw, loading, refetch } = useSwitch(switchId);
  const { defaultSwitch, loading: defaultLoading } = useDefaultSwitch(accountId);
  // Only the Overview tab (delete) needs to know whether numbers are attached
  // Saving changes for a shared switch asks for confirmation first
  const { count, isShared } = useSwitchNumberCount(switchId);
  // The numbers listed in the confirmation dialog (first page only)
  const { data: affectedData } = useQuery(GET_DIDS_FOR_SWITCH, {
    variables: { id: switchId, first: AFFECTED_LIST_SIZE },
    skip: !switchId || !isShared,
  });
  const affectedNumbers: AffectedNumbers | undefined = isShared
    ? {
        count,
        numbers: affectedData?.didsForSwitch?.edges.map(({ node }) => node.number) ?? [],
      }
    : undefined;

  const { hasDids, loading: checkingDids } = useHasDids(
    'switch',
    tab === 'overview' ? switchId : undefined
  );

  const isDefault = !!sw && sw.id === defaultSwitch?.id;
  const canUpdate = POLICIES['switches:update'](user);

  const renderTab = (s: NonNullable<typeof sw>, account: string) => {
    switch (tab) {
      case 'forwarding':
        // The forwarding forms take a full Switch; the query selects every field they read
        return (
          <>
            <SharedSwitchBanner
              switchId={s.id}
              numbersHref={switchTabPath(account, s.id, 'numbers')}
            />
            <SmsForward
              didSwitch={s as unknown as Mercury.Models.Switch}
              refetch={refetch}
              canUpdate={canUpdate}
              affectedNumbers={affectedNumbers}
            />
            <MmsForward
              didSwitch={s as unknown as Mercury.Models.Switch}
              refetch={refetch}
              canUpdate={canUpdate}
              affectedNumbers={affectedNumbers}
            />
          </>
        );
      case 'numbers':
        return POLICIES['dids:index'](user) ? (
          <DidsForResourceTable resource="switch" resourceId={s.id} accountId={account} />
        ) : (
          <FallbackCard description="You do not have permissions to view numbers." />
        );
      default:
        return (
          <>
            <UpdateSwitchForm
              key={`${s.name}|${s.notes ?? ''}`}
              switchId={s.id}
              name={s.name}
              notes={s.notes}
              canUpdate={canUpdate}
              refetch={refetch}
              affectedNumbers={affectedNumbers}
            />
            {POLICIES['switches:delete'](user) && !defaultLoading && (
              <DeleteSwitch
                switchId={s.id}
                name={s.name}
                accountId={account}
                hasDids={hasDids}
                checkingDids={checkingDids}
                numbersHref={switchTabPath(account, s.id, 'numbers')}
                isDefault={isDefault}
              />
            )}
          </>
        );
    }
  };

  return (
    <MainView
      loading={loading}
      title={loading ? 'Loading...' : `Switch: ${sw?.name ?? ''}`}
      description={SWITCH_EXPLANATION}
    >
      <SwitchTabNav />
      <Authorization
        policyCheck={POLICIES['switches:show'](user)}
        fallbackView={<FallbackCard description="You do not have permissions to view switches." />}
      >
        {sw && accountId ? (
          <div className="flex flex-col gap-6">{renderTab(sw, accountId)}</div>
        ) : !loading ? (
          <FallbackCard description="Switch not found." />
        ) : null}
      </Authorization>
    </MainView>
  );
};

export default AccountSwitchDetail;
